// ============================================================
// unsavedChanges.ts — the app-wide unsaved-changes registry.
//
// Explicit-save screens register while they hold unsaved edits
// (hooks/useUnsavedChangesGuard). Every way of leaving asks confirmLeave()
// first: navigateTo / navigateWithin (in-app navigation, section switches),
// the popstate handler in App.tsx (browser Back/Forward), and the screen's
// own × / Cancel via the hook's guard(). While anything is dirty it shows
// UnsavedChangesDialog ("Stay" / "Discard changes") and resolves true only
// on Discard. Refresh / tab close use the browser's native beforeunload
// prompt (the hook installs it) — browsers allow nothing else there.
// ============================================================
import { useSyncExternalStore } from 'react'
import { getTimerNavGuard } from '../utils/timerNavGuard'

const dirty = new Set<symbol>()

export const markDirty = (id: symbol, isDirty: boolean): void => {
  if (isDirty) dirty.add(id)
  else dirty.delete(id)
}

export const hasUnsavedChanges = (): boolean => dirty.size > 0

// ── The pending confirm (one at a time; concurrent asks share it) ──
let pending: { promise: Promise<boolean>; resolve: (leave: boolean) => void } | null = null
const listeners = new Set<() => void>()
const emit = (): void => { listeners.forEach((l) => l()) }

export const confirmLeave = (): Promise<boolean> => {
  if (!hasUnsavedChanges()) return Promise.resolve(true)
  if (pending) return pending.promise
  let resolve!: (leave: boolean) => void
  const promise = new Promise<boolean>((r) => { resolve = r })
  pending = { promise, resolve }
  emit()
  return promise
}

// Called by the dialog. Discard forgets the dirty marks (the screens holding
// them are about to close), so the navigation that follows isn't asked again.
export const answerLeave = (leave: boolean): void => {
  const p = pending
  if (!p) return
  pending = null
  if (leave) dirty.clear()
  emit()
  p.resolve(leave)
}

export const useLeaveConfirmOpen = (): boolean =>
  useSyncExternalStore(
    (l) => { listeners.add(l); return () => { listeners.delete(l) } },
    () => pending !== null,
  )

// Every in-app way of leaving asks BOTH guards: an open timer modal with
// unsaved time (its own "timer running" popup — utils/timerNavGuard) and
// then any explicit-save screen with unsaved edits. Resolves true = leave.
export const confirmLeaveAll = async (): Promise<boolean> => {
  const timer = getTimerNavGuard()
  if (timer) {
    const go = await new Promise<boolean>((resolve) => timer(() => resolve(true), () => resolve(false)))
    if (!go) return false
  }
  return confirmLeave()
}
